/**
 * Shared page-head (SEO) mechanism.
 *
 * `mapSeoToHeadTags` is a PURE function that maps a `CmsSeo` record into a flat
 * list of head-tag descriptors (title, meta, link). It is the single mapping
 * the dependent SEO/AEO task extends (e.g. adding JSON-LD from
 * `structuredDataType`). `applyHeadTags` writes those descriptors to the live
 * <head>; `PageHead` (component) calls both inside an effect.
 */
import { CmsSeo } from "@interfaces";

export interface HeadTag {
    /** DOM tag to create. */
    tag: "title" | "meta" | "link" | "script";
    /** Text content (title text, or JSON-LD body for script tags). */
    content?: string;
    /** Attributes to set on the element. */
    attrs?: Record<string, string>;
    /**
     * Stable key used to dedupe/replace a managed tag. Also written as the
     * `data-rh-cms` attribute so `applyHeadTags` can clean up prior tags.
     */
    key: string;
}

export interface SeoHeadOptions {
    /** Absolute site origin used to derive a canonical when none is supplied. */
    origin?: string;
    /** Current path (pathname), used to derive a canonical when none is supplied. */
    path?: string;
    /** Site/brand name appended to og:site_name. */
    siteName?: string;
    /** og:type, e.g. "website" or "article". */
    ogType?: string;
    /** Fallback og:image/twitter:image when the SEO record supplies none. */
    defaultOgImage?: string;
    /** twitter:site handle (with leading @). */
    twitterHandle?: string;
}

/** Default scope for page-level managed tags. */
export const MANAGED_ATTR = "data-rh-cms";
/** Scope for site-wide managed tags (never cleared by page-level re-applies). */
export const SITE_MANAGED_ATTR = "data-rh-site";

const trimOrigin = (origin?: string): string => (origin ?? "").replace(/\/$/, "");

/** Derive an absolute canonical URL from origin + path when SEO omits one. */
export const deriveCanonical = (seo: CmsSeo, opts?: SeoHeadOptions): string | undefined => {
    if (seo.canonicalUrl) return seo.canonicalUrl;
    const origin = trimOrigin(opts?.origin);
    if (!origin || !opts?.path) return undefined;
    const path = opts.path.startsWith("/") ? opts.path : `/${opts.path}`;
    return `${origin}${path}`;
};

/**
 * Pure mapping from CMS SEO fields to head-tag descriptors. No DOM access.
 * The dependent SEO task extends the returned array (e.g. JSON-LD script tags).
 */
export const mapSeoToHeadTags = (seo: CmsSeo, opts?: SeoHeadOptions): HeadTag[] => {
    const tags: HeadTag[] = [];

    if (seo.title) {
        tags.push({ tag: "title", content: seo.title, key: "title" });
        tags.push({ tag: "meta", attrs: { property: "og:title", content: seo.ogTitle ?? seo.title }, key: "og:title" });
        tags.push({ tag: "meta", attrs: { name: "twitter:title", content: seo.ogTitle ?? seo.title }, key: "twitter:title" });
    }

    if (seo.metaDescription) {
        tags.push({ tag: "meta", attrs: { name: "description", content: seo.metaDescription }, key: "description" });
        tags.push({
            tag: "meta",
            attrs: { property: "og:description", content: seo.ogDescription ?? seo.metaDescription },
            key: "og:description",
        });
        tags.push({
            tag: "meta",
            attrs: { name: "twitter:description", content: seo.ogDescription ?? seo.metaDescription },
            key: "twitter:description",
        });
    }

    const canonical = deriveCanonical(seo, opts);
    if (canonical) {
        tags.push({ tag: "link", attrs: { rel: "canonical", href: canonical }, key: "canonical" });
        tags.push({ tag: "meta", attrs: { property: "og:url", content: canonical }, key: "og:url" });
    }

    const image = seo.ogImage ?? opts?.defaultOgImage;
    if (image) {
        tags.push({ tag: "meta", attrs: { property: "og:image", content: image }, key: "og:image" });
        tags.push({ tag: "meta", attrs: { name: "twitter:image", content: image }, key: "twitter:image" });
        tags.push({ tag: "meta", attrs: { name: "twitter:card", content: "summary_large_image" }, key: "twitter:card" });
    }

    if (opts?.twitterHandle) {
        tags.push({ tag: "meta", attrs: { name: "twitter:site", content: opts.twitterHandle }, key: "twitter:site" });
    }

    tags.push({ tag: "meta", attrs: { property: "og:type", content: opts?.ogType ?? "website" }, key: "og:type" });

    if (opts?.siteName) {
        tags.push({ tag: "meta", attrs: { property: "og:site_name", content: opts.siteName }, key: "og:site_name" });
    }

    if (seo.keywords && seo.keywords.length) {
        tags.push({ tag: "meta", attrs: { name: "keywords", content: seo.keywords.join(", ") }, key: "keywords" });
    }

    if (seo.noindex) {
        tags.push({ tag: "meta", attrs: { name: "robots", content: "noindex, nofollow" }, key: "robots" });
    }

    return tags;
};

/**
 * Map one or more JSON-LD objects into managed `<script type="application/ld+json">`
 * head-tag descriptors. Falsy entries are skipped so callers can pass conditional
 * nodes (e.g. `buildBreadcrumbJsonLd(...)` which may return null) without guarding.
 */
export const mapJsonLdToHeadTags = (
    jsonLd: Array<Record<string, any> | null | undefined> | Record<string, any> | null | undefined,
): HeadTag[] => {
    const nodes = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(
        (n): n is Record<string, any> => !!n && typeof n === "object",
    );
    return nodes.map((node, i) => ({
        tag: "script",
        attrs: { type: "application/ld+json" },
        content: JSON.stringify(node),
        key: `jsonld-${(node["@type"] as string) || "node"}-${i}`,
    }));
};

/**
 * Remove managed head tags previously applied within a scope. Page-level tags
 * (default `data-rh-cms`) and site-wide tags (`data-rh-site`) are separate
 * scopes so a page re-apply never clobbers the site-wide Organization/WebSite.
 */
export const clearManagedHeadTags = (doc: Document = document, scope: string = MANAGED_ATTR): void => {
    doc.querySelectorAll(`[${scope}]`).forEach((el) => el.parentElement?.removeChild(el));
};

/** Write head-tag descriptors into the live document head (idempotent per render). */
export const applyHeadTags = (
    tags: HeadTag[],
    doc: Document = document,
    scope: string = MANAGED_ATTR,
): void => {
    if (!doc || !doc.head) return;
    clearManagedHeadTags(doc, scope);
    tags.forEach((t) => {
        if (t.tag === "title") {
            if (t.content) doc.title = t.content;
            return;
        }
        const el = doc.createElement(t.tag);
        el.setAttribute(scope, t.key);
        Object.entries(t.attrs ?? {}).forEach(([k, v]) => el.setAttribute(k, v));
        if (t.tag === "script" && t.content) el.textContent = t.content;
        doc.head.appendChild(el);
    });
};
